import React, { useCallback, useEffect, useState } from "react";
import DatePicker from "react-multi-date-picker";
import { ReactComponent as CalendarIcon } from "../../assets/img/common/calendar-dark-icon.svg";
import notify from "../common/notify";
import { TbClockHour3 } from "react-icons/tb";
import { ReactComponent as RemoveRowIcon } from "../../assets/img/common/trash-red-icon.svg";
import { addTimeToDate } from "../../utils/dateTool";

// TimeTableItemMobile component for rendering a timeline item on mobile view
const TimeTableItemMobile = ({ item, index, formik }) => {
  // State for controlling the visibility of task details
  const [showDetailsInput, setShowDetailsInput] = useState(false);
  // Destructuring item properties
  let { name, start_date, due_date, duration, detail } = item;
  // Helper function for updating formik values
  const fomrikSetter = (val) => {
    formik.setFieldValue("timeline", val);
  };
  // Destructuring error messages from formik or assigning empty object
  let {
    name: name_error,
    due_date: due_date_error,
    detail: details_error,
  } = formik?.errors?.timeline?.[index] ?? {};

  // Function to calculate the number of days between two dates
  function getDaysBetweenDates(dateString1, dateString2) {
    const date1 = new Date(dateString1);
    const date2 = new Date(dateString2);
    const diffInMs = date2.getTime() - date1.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    return Number(Math.max(1, diffInDays));
  }

  // Handler for changing the start date
  const handleChangeDateRange = (due) => {
    let timelineTableClone = [...formik.values.timeline];
    timelineTableClone[index].start_date = due?.format();
    fomrikSetter(timelineTableClone);

    const currentDueDate = timelineTableClone[index].due_date;
    if(currentDueDate) {
      handleChangeEndDate({ format: () => currentDueDate });
    }
  };

  // Handler for changing the end date
  const handleChangeEndDate = (due) => {
    let timelineTableClone = [...formik.values.timeline];
    timelineTableClone[index].due_date = due?.format();
    fomrikSetter(timelineTableClone);
    timelineTableClone[index].duration =
      getDaysBetweenDates(
          timelineTableClone?.[index]?.start_date ??
          timelineTableClone?.[index]?.due_date ??
          formik?.values?.createdAt ??
          new Date().toISOString(),
        due?.format()
      ) || 0;

    timelineTableClone = timelineTableClone?.map((item, timelineIndex, arr) => {
      let item_due_date = item?.due_date ? new Date(item?.due_date) : 0;
      if (timelineIndex > 0) {
        return {
          ...item,
          duration:
            getDaysBetweenDates(
              arr[timelineIndex ]?.start_date,
              item_due_date
            ) || 0,
        };
      } else return item;
    });
    // Update formik values
    fomrikSetter(timelineTableClone);
  };

  // Handler for changing task details
  const handleTaskChange = (e, index, field) => {
    let timelineTableClone = [...formik.values.timeline];
    timelineTableClone[index][field] = e.target.value;
    // Update formik values
    fomrikSetter(timelineTableClone);
  };

  // Handler for deleting a table item
  const handleDeleteTableItem = (index) => {
    let timelineTableClone = [...formik.values.timeline];
    timelineTableClone.splice(index, 1);
    // Update formik values
    fomrikSetter(timelineTableClone);
  };

  const changeDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  };

  return (
    <div
      className={`w-full flex flex-col items-center py-3 px-3 ${
        showDetailsInput ? "rounded-2xl" : "rounded-2xl"
      } bg-custom-gray-200 mb-7`}
    >
      <div className="w-full flex flex-col gap-3 items-center ">
        <section className="flex h-full items-center w-full relative ">
          {/* NumberItem component for displaying item index */}
          <NumberItem index={index} />
          {/* Input for task name */}
          <input
            id={"taskname" + index}
            value={name}
            onChange={(e) => handleTaskChange(e, index, "name")}
            placeholder="Task Name"
            type="text"
            className="input-without-style h-10 focus:bg-[#f6f3f3] hover:bg-[#f4f1f1] transition-all duration-200 rounded-md px-3 w-full mr-2"
          />
        </section>
        {/* Display name error if touched and an error exists */}
        {formik.touched?.timeline && name_error ? (
          <span
            className={`w-full text-red-500 text-sm mt-1 field-error-msg whitespace-nowrap pl-1`}
          >
            {name_error}
          </span>
        ) : null}
        <Separator flow="row" />
        <section className="flex items-center justify-between h-full w-full relative ">
          {/* DtPickerInput component for selecting date range */}
          <DtPickerInput
            minDate={addTimeToDate({
              date: formik.values.timeline?.[index - 1]?.due_date || new Date('2020.01.01'),
            })}
            maxDate={changeDays(new Date(formik.values.timeline?.[index]?.due_date), 1) || addTimeToDate({ days: 365 })}
            isInvalid={formik.touched?.timeline && due_date_error}
            inputContainerClass="w-[60%] h-9"
            id={"dateRange" + index}
            onChange={(e) => handleChangeDateRange(e)}
            value={start_date}
            placeholder={"Select Start"}
          />
          <DtPickerInput
            minDate={addTimeToDate({
              date: start_date,
            })}
            isInvalid={formik.touched?.timeline && due_date_error}
            inputContainerClass="w-[60%] h-9"
            id={"dateRange" + index}
            onChange={(e) => handleChangeEndDate(e)}
            value={due_date || ''}
            placeholder={"Select End"}
          />
          {/* Display duration with clock icon */}
          <span className="min-w-fit flex items-center justify-center font-medium whitespace-nowrap pr-1">
            <TbClockHour3 className="mr-1 " size={20} color="#3C2166" />{" "}
            {duration < 1
              ? "0 days":
              duration <= 1
              ? "1 day"
              : duration > 1
              ? duration + " days"
              : "1 day"}
          </span>
        </section>
        <Separator flow="row" />

        <div className="w-full flex items-center justify-between">
          {/* Button to toggle details input visibility */}
          <button
            type="button"
            onClick={() => setShowDetailsInput((prev) => !prev)}
            className="min-w-fit flex items-center  rounded-full group relative poppins-font"
          >
            <span className="text-sm font-medium mr-2 z-10 text-[#242424]">
              {showDetailsInput ? "Hide Details" : "Add Details"}
            </span>
            <span className="text-xl rounded-full flex justify-center items-center text-[#3C2166] ">
              {showDetailsInput ? "-" : "+"}
            </span>
          </button>
          {/* Button to delete the table item */}
          <button
            type="button"
            onClick={() => handleDeleteTableItem(index)}
            className="flex justify-center items-center text-[15px] p-1 border border-red-500 text-red-500 px-2 rounded-lg"
          >
            <RemoveRowIcon className="mr-1 h-5" />
            Remove Item
          </button>{" "}
        </div>
        {/* Display due date error if touched and an error exists */}
        {formik.touched?.timeline && due_date_error ? (
          <span
            className={`w-full text-red-500 text-sm mt-1 field-error-msg whitespace-nowrap pl-1`}
          >
            {due_date_error}
          </span>
        ) : null}
      </div>
      {/* Details input section */}
      {showDetailsInput && (
        <div className="w-full flex flex-col relative mt-5 pb-5">
          <span className="font-bold mb-2">Details</span>
          {/* Textarea for entering task details */}
          <textarea
            className="w-full rounded-lg px-3 py-2 h-20 bg-[#f2f2f2]"
            placeholder="task detail ... "
            onChange={(e) => handleTaskChange(e, index, "detail")}
            value={detail}
          ></textarea>
          {/* Display details error if touched and an error exists */}
          {formik.touched?.timeline && details_error ? (
            <span
              className={`w-full text-red-500 text-sm mt-1 field-error-msg whitespace-nowrap pl-1`}
            >
              {details_error}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};

const TimeTableItemDesktop = ({ item, index, formik }) => {
  // State for controlling the visibility of task details
  const [showDetailsInput, setShowDetailsInput] = useState(false);
  // Destructuring item properties
  let { name, start_date, due_date, duration, detail } = item;
  // Helper function for updating formik values
  const fomrikSetter = (val) => {
    formik.setFieldValue("timeline", val);
  };
  // Destructuring error messages from formik or assigning empty object
  let {
    name: name_error,
    due_date: due_date_error,
    detail: details_error,
  } = formik?.errors?.timeline?.[index] ?? {};

  // Function to calculate the number of days between two dates
  function getDaysBetweenDates(dateString1, dateString2) {
    const date1 = new Date(dateString1);
    const date2 = new Date(dateString2);

    const diffInMs = date2.getTime() - date1.getTime();
    // Convert milliseconds to days
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    return Number(Math.max(1, diffInDays));
  }

  // Handler for changing the start date
  const handleChangeDateRange = (due) => {
    let timelineTableClone = [...formik.values.timeline];
    timelineTableClone[index].start_date =( new Date(due?.format())).toISOString();
    fomrikSetter(timelineTableClone);
    console.log(timelineTableClone)


    const currentDueDate = timelineTableClone[index].due_date;
    if(currentDueDate) {
      handleChangeEndDate({ format: () => currentDueDate });
    }
  };

  // Handler for changing the end date
  const handleChangeEndDate = (due) => {
    let timelineTableClone = [...formik.values.timeline];
    timelineTableClone[index].due_date = due?.format();
    fomrikSetter(timelineTableClone);
    timelineTableClone[index].duration =
      getDaysBetweenDates(
          timelineTableClone?.[index]?.start_date ??
          timelineTableClone?.[index]?.due_date ??
          formik?.values?.createdAt ??
          new Date().toISOString(),
        due?.format()
      ) || 0;

    timelineTableClone = timelineTableClone?.map((item, timelineIndex, arr) => {
      let item_due_date = item?.due_date ? new Date(item?.due_date) : 0;
      if (timelineIndex > 0) {
        return {
          ...item,
          duration:
            getDaysBetweenDates(
              arr[timelineIndex ]?.start_date,
              item_due_date
            ) || 0,
        };
      } else return item;
    });
    console.log(timelineTableClone)
    // Update formik values
    fomrikSetter(timelineTableClone);
  };

  // Handler for changing task details
  const handleTaskChange = (e, index, field) => {
    let timelineTableClone = [...formik.values.timeline];
    timelineTableClone[index][field] = e.target.value;
    // Update formik values
    fomrikSetter(timelineTableClone);
  };

  // Handler for deleting a table item
  const handleDeleteTableItem = (index) => {
    let timelineTableClone = [...formik.values.timeline];
    timelineTableClone.splice(index, 1);
    // Update formik values
    fomrikSetter(timelineTableClone);
  };

  const changeDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() - days);
    return result;
  };

  return (
    <div
      className={`w-full flex flex-col items-center py-2 px-3 ${
        showDetailsInput ? "rounded-2xl" : "rounded-full"
      } bg-custom-gray-200 mb-7`}
    >
      <div className="w-full flex justify-between items-center h-10">
        <section className="flex h-full items-center min-w-[100px] w-[50%] relative ">
          <NumberItem index={index} />
          <input
            id={"taskname" + index}
            value={name}
            onChange={(e) => handleTaskChange(e, index, "name")}
            placeholder="Task Name"
            type="text"
            className="input-without-style h-full focus:bg-[#f6f3f3] hover:bg-[#f4f1f1] transition-all duration-200 rounded-md px-3 w-full mr-2"
          />
          {formik.touched?.timeline && name_error ? (
            <span
              className={`text-red-500 text-sm  mt-1 field-error-msg absolute -bottom-[29px] whitespace-nowrap pl-1`}
            >
              {name_error}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() => setShowDetailsInput((prev) => !prev)}
            className=" min-w-fit flex items-center  rounded-full group relative ml-2 poppins-font"
          >
            <span className="text-sm font-medium mr-2 z-10 text-[#242424]">
              {showDetailsInput ? "Hide Details" : "Add Details"}
            </span>
            <span className="text-xl rounded-full flex justify-center items-center text-[#3C2166] ">
              {showDetailsInput ? "-" : "+"}
            </span>
          </button>
        </section>

        <section className=" flex items-center justify-between h-full min-w-[50%] relative ">
          <Separator />
          <DtPickerInput
            minDate={addTimeToDate({
              date: formik.values.timeline?.[index - 1]?.due_date || new Date('2020.01.01'),
            })}
            maxDate={changeDays(new Date(formik.values.timeline?.[index]?.due_date), 1) || addTimeToDate({ days: 365 })}
            isInvalid={formik.touched?.timeline && due_date_error}
            inputContainerClass="w-[60%] h-9"
            id={"dateRange" + index}
            onChange={(e) => handleChangeDateRange(e)}
            value={start_date}
            placeholder={"Select Start"}
          />
          <DtPickerInput
            minDate={addTimeToDate({
              date: start_date,
            })}
            
            isInvalid={formik.touched?.timeline && due_date_error}
            inputContainerClass="w-[60%] h-9"
            id={"dateRange" + index}
            onChange={(e) => handleChangeEndDate(e)}
            value={due_date || ''}
            placeholder={"Select End"}
          />
          <Separator />
          
          <span className="min-w-[9rem] max-w-[40%] flex items-center justify-center font-medium whitespace-nowrap">
            <TbClockHour3 className="mr-1 " size={20} color="#3C2166" />{" "}
            {/* {item.duration} */}
            {duration < 1
              ? "0 days":
              duration <= 1
              ? "1 day"
              : duration > 1
              ? duration + " days"
              : "1 day"}
          </span>
          <button
            type="button"
            onClick={() => handleDeleteTableItem(index)}
            className="min-w-[32px] min-h-[32px] rounded-full text-sm flex justify-center items-center ml-3"
          >
            <RemoveRowIcon />
          </button>
          {formik.touched?.timeline && due_date_error ? (
            <span
              className={`text-red-500 text-sm left-2  mt-1 field-error-msg absolute -bottom-[29px] whitespace-nowrap pl-1`}
            >
              {due_date_error}
            </span>
          ) : null}
        </section>
      </div>
      {showDetailsInput && (
        <div className="w-full flex flex-col relative mt-5 pb-5">
          <span className="font-bold mb-2">Details</span>
          <textarea
            className="w-full rounded-lg px-3 py-2 h-20 bg-[#f2f2f2]"
            placeholder="task detail ... "
            onChange={(e) => handleTaskChange(e, index, "detail")}
            value={detail}
          ></textarea>
          {formik.touched?.timeline && details_error ? (
            <span
              className={`text-red-500 text-sm left-2 mt-1 field-error-msg absolute -bottom-[29px] whitespace-nowrap pl-1`}
            >
              {details_error}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
};


// TimelineTable component for managing project timelines
const TimelineTable = ({ formik }) => {
  // Function to check if fields in the timeline are valid
  const checkFieldsValue = () => {
    if (formik?.values?.timeline?.length === 0) {
      return true;
    }
    const regexPattern =
      /^(\d{4}\/\d{2}\/\d{2}|\d{2}\/\d{2}\/\d{4}|[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}\.\d{3}Z)$/;

    let timelineTableClone = [...formik.values.timeline];
    let lastIndex =
      formik.values?.timeline?.length > 0
        ? formik.values?.timeline?.length - 1
        : 0;
    let { start_date, due_date, name } = timelineTableClone[lastIndex];
    if (start_date === "" || due_date === ""){
      formik.setFieldTouched("timeline", true);
      return false;
    }
    if (
      regexPattern.test(new Date(due_date).toISOString()) &&
      regexPattern.test(new Date(start_date).toISOString()) &&
      name?.length &&
      !formik?.errors?.timeline
    ) {
      return true;
    } else {
      formik.setFieldTouched("timeline", true);
      return false;
    }
  };

  // Handler for adding a new item to the timeline
  const handleAddTableItem = () => {
    let timelineTableClone = [...formik.values.timeline];
    let lastIndex =
      formik.values?.timeline?.length > 0
        ? formik.values?.timeline?.length - 1
        : 0;
    if (checkFieldsValue()) {
      timelineTableClone.push({
        name: "",
        start_date: "",
        due_date:"",
        duration: 0,
      });
      formik.setFieldTouched("timeline", false);
      formik.setFieldValue("timeline", timelineTableClone);
    } else {
      notify("Please fill in all the fields correctly", "error");
    }
  };

  // Function to calculate total duration and update formik field
  const calcTotalDuration = useCallback(() => {
    let timelineClone = [...formik.values.timeline];
    let totalDuration = timelineClone.reduce((acc, task) => {
      return Number(acc) + Number(task.duration);
    }, 0);
    formik.setFieldValue("total_duration", totalDuration);
  }, [formik.values.timeline]);

  // Effect to recalculate total duration when timeline changes
  useEffect(() => {
    calcTotalDuration();
  }, [formik.values.timeline]);

  // Extracting timeline error for displaying error messages
  let timeline_error = formik?.errors?.timeline;

  return (
    <div id="timeline" className="w-full flex flex-col">
      <h3 className="mt-14 text-xl abel-font font-semibold">
        Project Timeline
      </h3>
      <p className="mt-2 mb-6">
        Provide a list of important events/phases of your project, set the start
        date or finish date, and the needed budget for each phase.
      </p>
      {/* Display error messages related to the timeline */}
      {formik?.errors?.timeline &&
      formik?.errors?.timeline?.some?.(
        (item) => item?.due_date === "Invalid due date!"
      ) ? (
        // <p className="text-red-500 mb-4">
        //   In the timeline, the date of each task must be{" "}
        //   <span className="font-bold">before</span> the date of the next task.
        // </p>
        <></>
      ) : typeof timeline_error === "string" &&
        formik?.touched?.timeline &&
        timeline_error ? (
        <p className="text-red-500 mb-4">{timeline_error}</p>
      ) : null}

      {/* Render desktop and mobile timeline items */}
      <div className="w-full hidden mlg:flex flex-col">
        {formik?.values?.timeline?.map((item, index) => (
          <TimeTableItemDesktop
            key={index}
            index={index}
            formik={formik}
            item={item}
          />
        ))}
      </div>
      <div className="w-full flex flex-col mlg:hidden">
        {formik?.values?.timeline?.map((item, index) => (
          <TimeTableItemMobile
            key={index}
            index={index}
            formik={formik}
            item={item}
          />
        ))}
      </div>

      {/* Button to add a new timeline item */}
      <div className="w-full flex justify-center items-center h-12 py-2 rounded-full bg-custom-gray-200">
        <button
          type="button"
          onClick={handleAddTableItem}
          className="flex items-center  rounded-full group relative"
        >
          <span className="w-8 h-8 rounded-full absolute left-0 top-0 group-hover:w-full group-hover:bg-white transition-all duration-300 ease-in"></span>
          <span className="w-8 h-8 rounded-full flex justify-center items-center z-10 bg-white text-[#3C2166] ">
            +
          </span>
          <span className="font-medium ml-2 z-10 pr-3">Add Timetables</span>
        </button>
      </div>

      {/* Display total duration at the end */}
      <div className="w-full flex items-center justify-end my-6 font-semibold">
        <span>Total Duration : </span>
        <span className="text-primary mr-4">
          {" "}
          {formik?.values?.total_duration || 0}{" "}
        </span>
      </div>
    </div>
  );
};

// ///////////////////////

const NumberItem = ({ index }) => {
  return (
    <span className="min-w-[32px] min-h-[32px] text-sm flex justify-center items-center rounded-full bg-primary text-white mr-3">
      {`${index + 1 < 10 && "0"}${index + 1}`}
    </span>
  );
};
const DtPickerInput = ({
  inputContainerClass = "w-full h-full",
  minDate,
  maxDate,
  placeholder,
  value,
  onChange,
  isInvalid,
}) => {
  return (
    <div className={`relative  ${inputContainerClass} `}>
      <span className="h-full absolute flex justify-center items-center  mlg:left-2 top-0">
        <CalendarIcon className={`w-5  pr-1`} />
        
      </span>

      <DatePicker
        dateSeparator=" - "
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        minDate={minDate}
        maxDate={maxDate}
        inputClass={` w-full h-full  custom-select-box ${
          isInvalid ? "text-red-500" : "focus:bg-[#f6f3f3] hover:bg-[#f4f1f1]"
        }   transition-all duration-200 rounded-md px-3 cursor-pointer font-[poppins] font-medium mlg:pl-9 mlg:pr-3 bg-transparent text-center
        `}
      />
    </div>
  );
};
const Separator = ({ flow = "col" }) => {
  return (
    <span
      className={` ${
        flow === "row" ? "w-full border-b h-1" : "w-1 mx-3 border-l-2 h-[95%]"
      }  border-custom-gray-500`}
    ></span>
  );
};

export default TimelineTable;
